import React, { useRef, useEffect } from 'react';
import maplibregl from 'maplibre-gl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STYLE_URL = `${API}/map/style`;
const LV_CENTER = [24.6, 56.88];

/**
 * Small map with a draggable pin for setting an event's precise location.
 * `lat`/`lng` drive the pin; dragging or clicking the map calls `onChange`.
 */
export const EventLocationMap = ({ lat, lng, onChange, height = 240 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const start = (lat != null && lng != null) ? [lng, lat] : LV_CENTER;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: start,
      zoom: (lat != null && lng != null) ? 13 : 6.4,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;

    const el = document.createElement('div');
    el.className = 'kolv-pin-marker';
    const marker = new maplibregl.Marker({ element: el, draggable: true, anchor: 'bottom' })
      .setLngLat(start).addTo(map);
    markerRef.current = marker;

    marker.on('dragend', () => {
      const p = marker.getLngLat();
      onChangeRef.current?.(+p.lat.toFixed(6), +p.lng.toFixed(6));
    });
    map.on('click', (e) => {
      marker.setLngLat(e.lngLat);
      onChangeRef.current?.(+e.lngLat.lat.toFixed(6), +e.lngLat.lng.toFixed(6));
    });

    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect external coordinate changes (city pick / address geocode) onto the pin.
  useEffect(() => {
    const map = mapRef.current, marker = markerRef.current;
    if (!map || !marker || lat == null || lng == null) return;
    const cur = marker.getLngLat();
    if (Math.abs(cur.lat - lat) < 1e-6 && Math.abs(cur.lng - lng) < 1e-6) return;
    marker.setLngLat([lng, lat]);
    map.easeTo({ center: [lng, lat], zoom: Math.max(map.getZoom(), 13), duration: 600 });
  }, [lat, lng]);

  return (
    <div ref={containerRef} className="w-full rounded-lg overflow-hidden border border-[#E5E4E0]"
      style={{ height }} data-testid="event-location-map" />
  );
};

export default EventLocationMap;
